/**
 * CertificationService — V96-3-1 / V96-3-3 / ADR-0045.
 *
 *   - certification profiles (what a workspace pursues: CR26 class / SOC 2 type)
 *   - timeframe arithmetic over FedRAMP rule units (hours … years, bizdays;
 *     business days exclude US federal holidays — utils/business-days.ts)
 *   - deadline provenance (V98-0-4): every timeframe-derived date carries
 *     the rule id, its source version and whether it is verified
 *   - lookups into `certification_timeframes` (never hard-coded numbers)
 *   - vulnerability remediation deadlines:
 *       FedRAMP CR26 profile → VDR-TFR-PVR by class × PAIN × exposure,
 *       otherwise the profile/workspace severity SLA.
 *     Unknown PAIN or exposure resolves to the *shortest* window in the
 *     table, so missing data can only make a deadline stricter.
 */
import { FRAMEWORK_IDS } from '../data/framework-ids.js';
import { US_FEDERAL_HOLIDAY_CALENDAR } from '../data/us-federal-holidays.js';
import { addBusinessDays } from '../utils/business-days.js';
import { queryInChunks } from '../utils/sql-in.js';
import { activeProgramKeys, getProgramManifest, profileProblem, programsWhere } from '../programs/manifests.js';
/** Active program keys in display order (planned programs can't be added). */
export const CERTIFICATION_PROGRAMS = Object.freeze(activeProgramKeys());
/** The rule-driven program (jurisdiction engine, ADR-0024): AI Regulatory. */
export const AI_REGULATORY_PROGRAM = (() => {
    const m = programsWhere((x) => x.readiness.engine === 'jurisdiction')[0];
    if (!m)
        throw new Error('The program registry has no jurisdiction-engine program.');
    return m.key;
})();
/** The framework an active program certifies against (null: rule-driven, e.g. AI Regulatory). */
export const PROGRAM_FRAMEWORK = Object.freeze(Object.fromEntries(programsWhere(() => true).map((m) => [m.key, m.frameworkId])));
/** True for an active program key. */
export function isCertificationProgram(v) {
    return typeof v === 'string' && CERTIFICATION_PROGRAMS.includes(v);
}
export class CertificationValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'CertificationValidationError';
    }
}
/** v9.6 serves Classes A–C (D-96-3). Class D is modelled but refused here. */
export const SUPPORTED_CLASSES = ['A', 'B', 'C'];
// ---- time arithmetic ---------------------------------------------------------
export function addTimeframe(start, num, unit) {
    const d = new Date(start.getTime());
    switch (unit) {
        case 'hours': return new Date(d.getTime() + num * 3_600_000);
        case 'days': return new Date(d.getTime() + num * 86_400_000);
        case 'weeks': return new Date(d.getTime() + num * 7 * 86_400_000);
        case 'bizdays': return addBusinessDays(d, num); // weekends + US federal holidays skipped (UTC)
        case 'months':
        case 'years': {
            const months = unit === 'years' ? num * 12 : num;
            const day = d.getUTCDate();
            d.setUTCDate(1);
            d.setUTCMonth(d.getUTCMonth() + months);
            const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
            d.setUTCDate(Math.min(day, lastDay)); // Jan 31 + 1 month → Feb 28/29, never Mar 3
            return d;
        }
    }
}
/** Walk a timeframe backwards (before-notices): the same calculator, negated. */
export function subtractTimeframe(start, num, unit) {
    return addTimeframe(start, -num, unit);
}
export function timeframeProvenance(ruleId, row) {
    if (!row)
        return { rule_id: ruleId, rule_version: null, verification: 'verification_pending', calendar: null };
    return {
        rule_id: row.rule_id,
        rule_version: row.source_version ?? null,
        // Active rows only land through an approved review; a row with no
        // reviewer (e.g. hand-seeded) is not treated as verified.
        verification: row.reviewer ? 'verified' : 'verification_pending',
        calendar: row.timeframe_type === 'bizdays' ? US_FEDERAL_HOLIDAY_CALENDAR : null,
    };
}
const DEFAULT_SLA = { critical: 15, high: 30, medium: 90, low: 180, info: 365 };
export async function createCertificationProfile(db, input) {
    const m = getProgramManifest(String(input.program));
    if (!m)
        throw new CertificationValidationError(`Unsupported program ${String(input.program)}.`);
    if (m.lifecycle !== 'active' && !input.allow_planned)
        throw new CertificationValidationError(`${m.label} is not available yet.`);
    const problem = profileProblem(m, input);
    if (problem)
        throw new CertificationValidationError(problem);
    const sla = { ...DEFAULT_SLA, ...(input.vuln_sla_days ?? {}) };
    for (const [k, v] of Object.entries(sla)) {
        if (!Number.isInteger(v) || v < 1 || v > 730)
            throw new CertificationValidationError(`vuln_sla_days.${k} must be 1–730 days.`);
    }
    const retention = input.retention_days ?? 400;
    if (retention < m.profile.minRetentionDays({ certification_class: input.certification_class ?? null })) {
        throw new CertificationValidationError('retention_days too short (Class C keeps ≥ 6 months of KSI metrics).');
    }
    const systems = input.system_ids ?? [];
    if (systems.length) {
        // Tenant plane (may be Postgres): chunked under D1's 100-parameter limit (distinct ids, so counts add up).
        const unique = [...new Set(systems)];
        const found = await queryInChunks(db, unique, (ph, chunk) => ({
            sql: `SELECT system_id FROM ai_systems WHERE workspace_id = ? AND system_id IN (${ph})`,
            params: [input.workspace_id, ...chunk],
        }));
        if (found.length !== unique.length)
            throw new CertificationValidationError('system_ids must belong to this workspace.');
    }
    const existing = await getActiveProfile(db, input.workspace_id, input.program);
    if (existing)
        throw new CertificationValidationError(`An active ${input.program} profile already exists; archive it first.`);
    const now = new Date().toISOString();
    await db.execute(`INSERT INTO certification_profiles (
       profile_id, workspace_id, program, certification_class, path, report_type,
       system_ids_json, target_date, vuln_sla_days_json, retention_days, status,
       created_by_user_id, created_at, updated_at, program_options_json
     ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'active', ?, ?, ?, ?)`, [
        input.profile_id, input.workspace_id, input.program, input.certification_class ?? null,
        input.path ?? null, input.report_type ?? null, JSON.stringify(systems), input.target_date ?? null,
        JSON.stringify(sla), retention, input.created_by_user_id, now, now, JSON.stringify(input.program_options ?? {}),
    ]);
    return (await getProfile(db, input.workspace_id, input.profile_id));
}
export async function getProfile(db, workspaceId, profileId) {
    const r = await db.query(`SELECT * FROM certification_profiles WHERE profile_id = ? AND workspace_id = ? LIMIT 1`, [profileId, workspaceId]);
    return r.results[0] ?? null;
}
export async function getActiveProfile(db, workspaceId, program) {
    const r = await db.query(`SELECT * FROM certification_profiles WHERE workspace_id = ? AND program = ? AND status = 'active' LIMIT 1`, [workspaceId, program]);
    return r.results[0] ?? null;
}
/**
 * The active profile whose manifest takes vulnerability deadlines from a rule
 * (`deadlines.vulnerability` other than the ops SLA: FedRAMP's VDR-TFR-PVR), if any.
 * Vulnerability scans in a workspace are dated against it.
 */
export async function getVulnerabilityRuleProfile(db, workspaceId) {
    for (const m of programsWhere((x) => x.deadlines.vulnerability !== 'ops_sla')) {
        const p = await getActiveProfile(db, workspaceId, m.key);
        if (p)
            return p;
    }
    return null;
}
export async function listProfiles(db, workspaceId) {
    const r = await db.query(`SELECT * FROM certification_profiles WHERE workspace_id = ? ORDER BY status, created_at DESC`, [workspaceId]);
    return r.results;
}
/**
 * The profile options a program's scope filter reads (ADR-0079 §7): the
 * applicability option and every option a `match` names (SOC 2: `categories`,
 * `privacy_role`). Empty for programs without a filter.
 */
export function scopeOptionKeys(program) {
    const a = getProgramManifest(program)?.readiness.applicability;
    if (!a)
        return [];
    return [...new Set([a.option, ...(a.match ?? []).map((x) => x.option)])];
}
/**
 * V106-1-12 / ADR-0078 §1: replace the scope options of an active profile
 * (SOC 2 Trust Services Categories and privacy role). Only the keys the
 * manifest's applicability filter reads can be set here; every other option
 * (a GovRAMP tier, a CMMC level) keeps its own flow. The merged options must
 * pass the manifest (`profileProblem`). A frozen period keeps the categories
 * its snapshot recorded. Null when the profile isn't this workspace's active
 * profile of a program with a scope filter (the route answers 404).
 */
export async function setProfileScopeOptions(db, workspaceId, profileId, scope, now = new Date()) {
    const r = await db.query(`SELECT * FROM certification_profiles WHERE profile_id = ? AND workspace_id = ? AND status = 'active' LIMIT 1`, [profileId, workspaceId]);
    const row = r.results[0];
    if (!row)
        return null;
    const keys = scopeOptionKeys(row.program);
    if (!keys.length)
        return null;
    const extra = Object.keys(scope).filter((k) => !keys.includes(k));
    if (extra.length)
        throw new CertificationValidationError(`Only ${keys.join(', ')} can be set here (not ${extra.join(', ')}).`);
    let current = {};
    try {
        const v = JSON.parse(row.program_options_json || '{}');
        if (v && typeof v === 'object' && !Array.isArray(v))
            current = v;
    }
    catch { /* unreadable stored options: replaced below */ }
    const merged = { ...current };
    for (const k of keys) {
        if (scope[k] === undefined || scope[k] === null)
            delete merged[k];
        else
            merged[k] = scope[k];
    }
    const m = getProgramManifest(row.program);
    const problem = profileProblem(m, { report_type: row.report_type, certification_class: row.certification_class, path: row.path, program_options: merged });
    if (problem)
        throw new CertificationValidationError(problem);
    await db.execute(`UPDATE certification_profiles SET program_options_json = ?, updated_at = ? WHERE profile_id = ? AND workspace_id = ? AND status = 'active'`, [JSON.stringify(merged), now.toISOString(), profileId, workspaceId]);
    return getProfile(db, workspaceId, profileId);
}
export async function archiveProfile(db, workspaceId, profileId) {
    const r = await db.execute(`UPDATE certification_profiles SET status = 'archived', updated_at = ? WHERE profile_id = ? AND workspace_id = ? AND status = 'active'`, [new Date().toISOString(), profileId, workspaceId]);
    return (r.meta?.changes ?? 0) > 0;
}
/** Most specific active row for a rule: exact path beats 'all'; class-specific beats class-less. */
export async function lookupTimeframe(db, ruleId, opts) {
    const rows = await db.query(`SELECT timeframe_id, rule_id, path, certification_class, pain, variant, force, timeframe_type, timeframe_num, source_version, reviewer
       FROM certification_timeframes
      WHERE program = ? AND rule_id = ? AND status = 'active'`, [FRAMEWORK_IDS.FEDRAMP_CR26, ruleId]);
    const matches = rows.results.filter((r) => (r.path === 'all' || r.path === opts.path) &&
        (r.certification_class === null || r.certification_class === opts.certificationClass) &&
        (opts.pain === undefined || r.pain === (opts.pain ?? null)) &&
        (opts.variant === undefined || r.variant === (opts.variant ?? null)));
    const score = (r) => (r.path === opts.path ? 2 : 0) + (r.certification_class ? 1 : 0);
    matches.sort((a, b) => score(b) - score(a));
    return matches[0] ?? null;
}
export function vdrExposure(v) {
    const lev = v.kev || v.likely_exploitable !== false; // unknown → LEV (conservative)
    if (!lev)
        return 'nlev';
    return v.internet_reachable === false ? 'nirv_lev' : 'irv_lev'; // unknown reachability → IRV
}
/**
 * ADR-0050 §2: one implementation per manifest `deadlines.vulnerability` key.
 * `VDR-TFR-PVR` reads the approved FedRAMP timeframes by class × PAIN ×
 * exposure; until they're approved it falls back to the ops SLA as a
 * placeholder marked verification_pending, never as the rule.
 */
export const VULNERABILITY_DEADLINE_POLICIES = {
    ops_sla: async (_db, profile, v, now) => opsSlaDeadline(profile, v, now, false),
    'VDR-TFR-PVR': async (db, profile, v, now) => {
        const start = v.first_seen ? new Date(v.first_seen) : now;
        if (profile && profile.certification_class) {
            const exposure = vdrExposure(v);
            const rows = await db.query(`SELECT timeframe_id, rule_id, path, certification_class, pain, variant, force, timeframe_type, timeframe_num, source_version, reviewer
           FROM certification_timeframes
          WHERE program = ? AND rule_id = 'VDR-TFR-PVR' AND certification_class = ? AND variant = ? AND status = 'active'`, [FRAMEWORK_IDS.FEDRAMP_CR26, profile.certification_class, exposure]);
            if (rows.results.length > 0) {
                const byPain = new Map(rows.results.map((r) => [r.pain, r]));
                const toDate = (r) => addTimeframe(start, r.timeframe_num, r.timeframe_type).getTime();
                const known = v.pain != null ? byPain.get(v.pain) : undefined;
                const row = known ?? rows.results.reduce((a, b) => (toDate(a) <= toDate(b) ? a : b));
                return {
                    deadline: addTimeframe(start, row.timeframe_num, row.timeframe_type),
                    policy: 'vdr',
                    pain: row.pain,
                    exposure,
                    provenance: timeframeProvenance('VDR-TFR-PVR', row),
                    basis: known
                        ? `VDR-TFR-PVR class ${profile.certification_class}, PAIN ${row.pain}, ${exposure}: ${row.timeframe_num} ${row.timeframe_type}`
                        : `VDR-TFR-PVR class ${profile.certification_class}, ${exposure}, PAIN not assessed — shortest window applied (${row.timeframe_num} ${row.timeframe_type})`,
                };
            }
        }
        // Profile exists but the rules batch isn't approved yet: the SLA as a placeholder.
        return opsSlaDeadline(profile, v, now, true);
    },
};
export async function vulnerabilityDeadline(db, profile, v, now) {
    const policy = (profile && getProgramManifest(profile.program)?.deadlines.vulnerability) || 'ops_sla';
    return VULNERABILITY_DEADLINE_POLICIES[policy](db, profile, v, now);
}
function opsSlaDeadline(profile, v, now, fedrampPending) {
    const start = v.first_seen ? new Date(v.first_seen) : now;
    const sla = { ...DEFAULT_SLA, ...(profile ? JSON.parse(profile.vuln_sla_days_json) : {}) };
    const days = v.kev ? Math.min(sla[v.severity] ?? 30, sla.critical) : sla[v.severity] ?? 30;
    return {
        deadline: addTimeframe(start, days, 'days'),
        policy: 'ops_sla',
        pain: null,
        exposure: null,
        provenance: fedrampPending
            ? timeframeProvenance('VDR-TFR-PVR', null)
            : { rule_id: `ops-sla:${v.kev ? 'kev' : v.severity}`, rule_version: profile?.updated_at ?? 'default', verification: 'verified', calendar: null },
        basis: `${v.severity} vulnerability SLA ${days} days${v.kev ? ' (KEV: critical window)' : ''}${fedrampPending ? ' — FedRAMP timeframes not yet approved (verification pending)' : ''}`,
    };
}
//# sourceMappingURL=certification-service.js.map